import React, { useState } from 'react';
import { Plus, Trash2, Edit } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import ImageUploader from './ImageUploader';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { createBlog, updateBlog, deleteBlog } from '../data/api';

const BLOG_CATEGORIES = ['Commercial', 'Retail', 'F&B', 'Carpentry', 'Tips', 'Residential', 'Industrial'];

const QUILL_MODULES = {
  toolbar: [
    [{ header: [2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['link'],
    ['clean'],
  ],
};

const BlogsTab = ({ blogs, onRefresh }) => {
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ title: '', slug: '', excerpt: '', content: '', coverImage: '', category: 'Commercial', author: 'Yusen Design Team', readTime: '', tags: '' });

  const handleEdit = (b) => {
    setEditId(b.id);
    setForm({ ...b, tags: (b.tags || []).join(', ') });
    setShowForm(true);
  };

  const handleSave = async () => {
    const data = { ...form, tags: form.tags.split(',').map(t => t.trim()).filter(Boolean) };
    if (editId) {
      await updateBlog(editId, data);
    } else {
      await createBlog(data);
    }
    setShowForm(false);
    setEditId(null);
    setForm({ title: '', slug: '', excerpt: '', content: '', coverImage: '', category: 'Commercial', author: 'Yusen Design Team', readTime: '', tags: '' });
    onRefresh();
  };

  const handleDelete = async (id) => {
    if (window.confirm('Delete this blog post?')) {
      await deleteBlog(id);
      onRefresh();
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-bold text-gray-900">{blogs.length} Blog Posts</h2>
        <Button onClick={() => { setShowForm(true); setEditId(null); }} data-testid="add-blog-btn" className="bg-blue-600 text-white">
          <Plus className="w-4 h-4 mr-1" /> Add Post
        </Button>
      </div>

      {showForm && (
        <div className="bg-gray-50 border rounded-xl p-5 mb-5 space-y-3" data-testid="blog-form">
          <div className="grid grid-cols-2 gap-3">
            <Input placeholder="Blog Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="blog-form-title" />
            <select className="border rounded-lg px-3 py-2 text-sm" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="blog-form-category">
              {BLOG_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <Input placeholder="URL Slug (auto-generated if empty)" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} data-testid="blog-form-slug" />
          <Textarea placeholder="Excerpt (short summary)" value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} rows={2} data-testid="blog-form-excerpt" />
          <div data-testid="blog-form-content">
            <label className="text-sm font-medium text-gray-700 block mb-1">Content</label>
            <ReactQuill theme="snow" value={form.content} onChange={(val) => setForm({ ...form, content: val })} modules={QUILL_MODULES} style={{ minHeight: 200 }} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Input placeholder="Author" value={form.author} onChange={(e) => setForm({ ...form, author: e.target.value })} />
            <Input placeholder="Read Time (e.g. 5 min read)" value={form.readTime} onChange={(e) => setForm({ ...form, readTime: e.target.value })} />
          </div>
          <ImageUploader label="Cover Image" value={form.coverImage} onChange={(url) => setForm({ ...form, coverImage: url })} />
          <Input placeholder="Tags (comma-separated)" value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} data-testid="blog-form-tags" />
          <div className="flex gap-2">
            <Button onClick={handleSave} data-testid="blog-form-save" className="bg-green-600 text-white">Save</Button>
            <Button variant="outline" onClick={() => { setShowForm(false); setEditId(null); }}>Cancel</Button>
          </div>
        </div>
      )}

      <div className="space-y-2">
        {blogs.map((b) => (
          <div key={b.id} className="bg-white border rounded-lg p-4 flex items-center gap-4 hover:shadow-sm transition-shadow" data-testid={`blog-row-${b.id}`}>
            {b.coverImage && <img src={b.coverImage} alt="" className="w-16 h-12 object-cover rounded" />}
            <div className="flex-1 min-w-0">
              <p className="font-medium text-gray-900 truncate">{b.title}</p>
              <p className="text-xs text-gray-500">{b.author} | {new Date(b.date).toLocaleDateString()} | {b.readTime}</p>
            </div>
            <Badge variant="secondary" className="text-xs">{b.category}</Badge>
            <div className="flex gap-1">
              <button onClick={() => handleEdit(b)} className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg" data-testid={`edit-blog-${b.id}`}><Edit className="w-4 h-4" /></button>
              <button onClick={() => handleDelete(b.id)} className="p-2 text-red-600 hover:bg-red-50 rounded-lg" data-testid={`delete-blog-${b.id}`}><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default BlogsTab;
